import styled from "styled-components"
import { pagePaddingCssRules } from "../../../shared_components/Header/styles"
import SegmentedControl from "../../../shared_components/Library/SegmentedControl"
import { Box } from "../../../shared_components/UI/Box";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../modules/user/selectors";
import { selectAccount } from "../../../modules/web3/selectors";
import useIsLoggedIn from "../../../hooks/useIsLoggedIn";
import useTwitterProfileImage from "../../../hooks/useTwitterProfileImage";
import { H1, H2, P, Strong } from "../../../shared_components/Library/Typeography";
import { obfuscateEthWalletAddress } from "../../../shared_components/SocialConnectButtons/Twitter";
import { useHistory } from "react-router-dom";
import routes from "../../../router/routes";
import { useGetMemecoinsCreatedByUserQuery, useGetMemecoinsHeldQuery } from "../../../modules/memecoins/api";
import { showDecimalsIfNeeded } from "../Allocations/InvestmentInfo";
import { getTransactionTokenAmount } from "../../Memecoins/TokenPage/helpers";
import { amountFromWei } from "../../../utils/amount";
import AnimatedLoader from "../../../shared_components/AnimatedLoader";
import { barrySurface, useShouldShowForThreshold } from "../../../theme";
import { getUsernameFromUserObject } from "../../DropDetails/DropOverview";
import WalletWidget from "./WalletWidget";

enum PortfolioType {
  Held = 'held',
  Created = 'created'
}

const UserDetails = () => {
  const user = useSelector(selectUser)
  const account = useSelector(selectAccount)
  // const { isLoggedIn, isAuthenticating } = useIsLoggedIn()
  const { url: profileImgUrl } = useTwitterProfileImage(user)

  const userName = getUsernameFromUserObject(user, account)

  return (
    <UserDetailsContainer ac g={8}>
      <UserAvatar src={profileImgUrl} alt="profile" />
      <Box g={8} dc padT={8}>
        <H1 s="2XL" uc>{userName}</H1>
        <P s="L">{obfuscateEthWalletAddress(account)}</P>
      </Box>
    </UserDetailsContainer>
  )
}

const Header = () => {
  const { push } = useHistory()
  const isAtleastMediumDisplay = useShouldShowForThreshold('compact')

  const handleOnClick = () => {
    push(routes.myzap.menu)
  }

  return (
    <Box g={8} jsb ac>
      <UserDetails />
      {!isAtleastMediumDisplay && (
        <SettingsButton
          onClick={handleOnClick}
        >
          <img src="/img/icons/buttons/setting.svg" alt="back" />
        </SettingsButton>
      )}
    </Box>
  )
}

const SettingsButton = styled.div`
  display: flex;
  width: var(--units-40, 40px);
  height: var(--units-40, 40px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 84px;
  border: 1px solid var(--stroke-secondary, #333);
`

const UserDetailsContainer = styled(Box)`
  align-items: center;
  flex-shrink: 0;
  height: 92px;
  align-self: stretch;
`

const UserAvatar = styled.img`
  width: 68px;
  height: 68px;

  border-radius: var(--units-88, 88px);
  border: 1px solid var(--stroke-secondary, #333);
  background: url(<path-to-image>) lightgray 50% / cover no-repeat, var(--surface-primary, #080808);
`

function Portfolio() {
  const [selectedType, setSelectedType] = useState<PortfolioType>(PortfolioType.Held)
  const heldRef = useRef(null)
  const createdRef = useRef(null)

  return (
    <Box dc g={32}>
      <Header />
      <WalletWidget />
      <Box ac jc dc g={16}>
        <SegmentedControl
          name="porfolio-control"
          segments={[
            { value: PortfolioType.Held, label: 'Held', ref: heldRef },
            { value: PortfolioType.Created, label: 'Created', ref: createdRef },
          ]}
          callback={(value) => {
            setSelectedType(value as PortfolioType)
          }}
          defaultIndex={selectedType === PortfolioType.Held ? 0 : 1}
        />
        {selectedType === PortfolioType.Held && <TokensHeld />}
        {selectedType === PortfolioType.Created && <TokensCreated />}
      </Box>
    </Box>
  )
}

const Container = styled(Box)`
  display: flex;
  gap: var(--units-16, 16px);
  flex-direction: column;
  ${pagePaddingCssRules}
  padding-top: 24px !important;
`

const TokensHeld = () => {
  const { data, isLoading, isFetching } = useGetMemecoinsHeldQuery({})
  const { push } = useHistory()

  if (isLoading || isFetching) {
    return (
      <Box c ac>
        <AnimatedLoader onlyWhite size={32} />
      </Box>
    )
  }

  const handleOnClick = (memecoinAddress: string) => () => {
    push(`${routes.myzap.tokensHeld}/${memecoinAddress}`)
  }

  return (
    <Table>
      <thead>
        <TableRow>
          <th>
            <P s="M" c="text-secondary" fw="regular">Coin</P>
          </th>
          <th>
            <P s="M" c="text-secondary" fw="regular">Your balance</P>
          </th>
        </TableRow>
      </thead>
      <tbody>
        {!!data?.memecoinsHeld?.length && data?.memecoinsHeld?.map(({ _id, memecoin, totalAmount }, index) => (
          <TableBodyRow
            key={`${_id}_token_held_${index}`}
            onClick={handleOnClick(memecoin.poolAddress)}
            bst="alpha"
            bss="enabled"
          >
            <td>
              <Box ac g={8}>
                <TokenLogo src={memecoin?.media?.logo?.url || '/meme-icon.png'} alt={memecoin.tokenName} />
                <Box g={2}>
                  <Strong uc s="L">{memecoin.tokenName}</Strong>
                </Box>
              </Box>
            </td>
            <BalanceContainer>
              <Strong uc s="L">{showDecimalsIfNeeded(
                Number(amountFromWei(totalAmount, memecoin.decimals).toFixed(0))
              )}</Strong>
              {' '}
              <P s="S">{memecoin.tokenSymbol}</P>
            </BalanceContainer>
          </TableBodyRow>
        ))}
      </tbody>
    </Table>
  )
}

const TokensCreated = () => {
  const { data, isLoading } = useGetMemecoinsCreatedByUserQuery({})

  const { push } = useHistory()

  if (isLoading) {
    return (
      <Box c ac>
        <AnimatedLoader onlyWhite size={32} />
      </Box>
    )
  }

  const handleOnClick = (memecoinAddress: string) => () => {
    push(`${routes.myzap.tokensHeld}/${memecoinAddress}`)
  }

  return (
    <Table>
      <thead>
        <TableRow>
          <td>
            <P s="M" c="text-secondary" fw="regular">Coin</P>
          </td>
          <td>
            <P s="M" c="text-secondary" fw="regular">Your balance</P>
          </td>
        </TableRow>
      </thead>
      <tbody>
        {!!data?.memecoins?.length && data?.memecoins?.map((memecoin) => (
          <TableBodyRow
            key={`${memecoin?._id}_token_created`} onClick={handleOnClick(memecoin.poolAddress)}
            bst="alpha"
            bss="enabled"
          >
            <td>
              <Box ac g={8}>
                <TokenLogo src={memecoin?.media?.logo?.url || '/meme-icon.png'} alt={memecoin.tokenName} />
                <Box g={2}>
                  <Strong uc s="L">{memecoin?.tokenName}</Strong>
                </Box>
              </Box>
            </td>
            <BalanceContainer>
              <Strong uc s="L">{showDecimalsIfNeeded(
                Number(amountFromWei(memecoin?.totalAmountHeld, memecoin?.decimals).toFixed(0))
              )}</Strong>
              {' '}
              <P s="S">{memecoin?.tokenSymbol}</P>
            </BalanceContainer>
          </TableBodyRow>
        ))}
      </tbody>
    </Table>
  )
}

const Table = styled.table`
  width: 100%;
`

const TokenLogo = styled.img`
  display: flex;
  width: 38px;
  height: 38px;
  align-items: flex-start;
  gap: 10px;
  border-radius: 88px;
  background: url(<path-to-image>) lightgray 50% / cover no-repeat;
`

const BalanceContainer = styled.td`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  white-space: nowrap;
  
  ${P} {
    margin-left: 4px; // Add a bit of space between the number and symbol if needed
  }
`;

const TableRow = styled.tr`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--units-8, 8px);
  flex: 1 0 0;

  
  th:not(:first-child) {
    align-self: flex-end;
    text-align: right;
  }
`

const TableBodyRow = styled(TableRow)`
  padding: var(--units-16, 16px) 0;
  border-bottom: 1px solid var(--stroke-tertiary, #252525);
  &:last-child {
    border-bottom: none;
  }
  cursor: pointer;

  ${barrySurface}
`

export default Portfolio
